import type { PostHog, Properties } from "posthog-js"

import fnv from "fnv-plus"
import yaml from "js-yaml"
import postHog from "posthog-js"
import { ref, watch } from "vue"

const env = import.meta.env

// Goals need to be set in posthog to be tracked.

// You can send any event to posthog, but these are the ones we are tracking.
// It's helpful to keep a list of them here for type safety and to avoid typos.
// If you add a new event, please add it to the enum.
export type EventString =
  | "$pageview"
  | "$pageleave"
  | "file_import_clicked"
  | "file_imported"
  | "add_dataset_to_table"
  | "add_new_dataset"
  | "app_load"
  | "create_category_for_relationship"
  | "edit_VariesBy"
  | "export_model"
  | "GDI_date_format_changed"
  | "GDI_date_format"
  | "GDI_file_match_channel"
  | "GDI_guessed_date_format"
  | "import_model"
  | "javaScript_error"
  | "log_out_clicked"
  | "redo_initiated"
  | "reset_attribute_value"
  | "save_formula"
  | "table_selected"
  | "undo_initiated"
  | "help_clicked"
  | "open_chat_beacon_clicked"
  | "website_clicked"
export type PropertyString = keyof Properties

export type CategoryName = string
export type HashedName = string
export type PlainTextName = string
export type HashedNamesMap = Map<HashedName, PlainTextName>

export interface HashedModelInfo {
  objectNames: HashedNamesMap // for top-level object names, e.g. lists, types, datasets
  categoryItemNames: Map<CategoryName, HashedNamesMap>
}

const SEED = env.VITE_PUBLIC_POSTHOG_HOST

function hash(input: string, seed?: string) {
  fnv.seed(seed || SEED)
  return fnv.hash(input, 32).str()
}

export function newHashedModelInfo(): HashedModelInfo {
  const objectNames = new Map<string, string>()
  const categoryItemNames = new Map<CategoryName, HashedNamesMap>()

  return { objectNames, categoryItemNames }
}

export async function hashModelDescriptor(
  localModel: string,
  seed: string
): Promise<HashedModelInfo> {
  const hashedModelInfo = newHashedModelInfo()
  const modelDesc: any = await yaml.load(localModel)

  // get top-level object names: lists/types/datasets/etc. go in one pool
  for (const modelObjectName in modelDesc) {
    if (Array.isArray(modelDesc[modelObjectName])) {
      for (const i in modelDesc[modelObjectName]) {
        const name = (modelDesc[modelObjectName][i] as any).name
        if (name) {
          hashedModelInfo.objectNames.set(hash(name, seed), name)
        }
      }
    }
  }

  for (const category in modelDesc.list_data) {
    if (category.endsWith("::All")) {
      const categoryItems =
        hashedModelInfo.categoryItemNames.get(category) ||
        new Map<string, string>()

      for (const item of modelDesc.list_data[category]) {
        categoryItems.set(hash(item, seed), item)
      }

      hashedModelInfo.categoryItemNames.set(category, categoryItems)
    }
  }
  return hashedModelInfo
}

const posthog = ref<PostHog>()

const userAnalyticsId = ref()

watch(userAnalyticsId,
  () => {
    if (userAnalyticsId.value) {
      posthog.value?.identify(userAnalyticsId.value)
    } else {
      posthog.value?.reset()
    }
  }
)

function trackEvent(
  name: EventString,
  properties: Record<string, any> = {},
  options: Record<string, any> = {}
) {
  if (env.VITE_KS_DEV_TOOLS === "true") {
    /* eslint-disable no-console */
    console.debug(`tracked: ${name}`)
    console.debug(`analyticsId: ${userAnalyticsId.value},`)
    console.debug(`properties: ${JSON.stringify(properties)}`)
    console.debug(`options: ${JSON.stringify(options)}`)
    /* eslint-enable no-console */
  }
  posthog.value?.capture(name, properties, options)
}

export default function useAnalytics() {
  const loaded = ref(false)

  function initialise() {
    if (!loaded.value) {
      const postHogKey = env.VITE_PUBLIC_POSTHOG_KEY
      const postHogHost = env.VITE_PUBLIC_POSTHOG_HOST

      if (!postHogKey || !postHogHost) {
        console.error("PostHog key or host is not defined in environment variables")
        return
      }

      posthog.value = postHog.init(postHogKey, {
        api_host: postHogHost,
        disable_persistence: true,
        persistence: "memory",
        session_recording: {
          maskAllInputs: true
        }

      }, "web-app")

      trackJavascriptErrors()

      loaded.value = true
    }
  }

  function trackJavascriptErrors() {
    try {
      window.addEventListener("error", (e) => {
        trackEvent("javaScript_error",
          {
            message: e.message,
            filename: e.filename,
            lineno: e.lineno,
            colno: e.colno,
            url: window.location.href,
          }
        )
      })

      window.addEventListener("unhandledrejection", (e) => {
        trackEvent("javaScript_error",
          {
            message: `Unhandled rejection (reason: ${
              e.reason?.stack || e.reason || "Unknown reason"
            }).`,
            url: window.location.href,
          }
        )
      })
    } catch {
      /* NOOP */
    }
  }

  function setUserAnalyticsId(analyticsId: string) {
    userAnalyticsId.value = analyticsId
  }

  return {
    userAnalyticsId,
    initialise,
    setUserAnalyticsId,
    trackEvent
  }
}
